import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  savingsVaultCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { savingsEndpoints } from "@/features/savings/api/endpoints";
import type {
  CreateSavingsVaultRequestDto,
  SavingsVaultDto,
} from "@/features/savings/api/dto";

/**
 * Ouvre un coffre d'épargne (`POST /SavingsVaults/add`, 201) — adossé à
 * l'un des portefeuilles principaux du titulaire (`mainWalletId`), dont il
 * reprend la devise. Nombre de coffres illimité par défaut
 * (`Savings__MaxVaultsPerOwner`).
 *
 * Idempotency-Key OBLIGATOIRE (guide §4.7) : la clé est persistée par
 * empreinte du corps (même tentative après coupure réseau / reload) ;
 * un 409 attend puis rejoue la même clé ; un 422 oublie la clé.
 */
export const useCreateSavingsVault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["savings", "savingsVaults", "add"],
    mutationFn: (body: CreateSavingsVaultRequestDto) =>
      mutateIdempotent<SavingsVaultDto>({
        scopeId: "savings.savingsVaults.add",
        url: savingsEndpoints.savingsVaults.add,
        body,
      }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, savingsVaultCacheKeys(), {
        resetListPage: true,
      }),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateSavingsVault failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "savings" }
      );
    },
  });
};
